import React from "react";
import { FileText, Users, MessageSquare, CreditCard } from "lucide-react";
import { z } from "zod";

const StepSchema = z.object({
	icon: z.any(),
	title: z.string(),
	description: z.string(),
	color: z.string(),
});

const HowItWorksSchema = z.object({
	header: z.string().default("How It Works"),
	description: z
		.string()
		.default(
			"From project posting to payment, we've streamlined the entire process to make BIM collaboration effortless."
		),
	buttonText: z.string().default("Start Your Project Today"),
	steps: z.array(StepSchema).default([
		{
			icon: FileText,
			title: "Post Requirements",
			description: "Describe scope, BIM level, timeline & budget.",
			color: "bg-primary-500",
		},
		{
			icon: Users,
			title: "Receive Bids & Proposals",
			description: "Smart matching algorithm invites the best-fit vendors.",
			color: "bg-secondary-500",
		},
		{
			icon: MessageSquare,
			title: "Collaborate in Real Time",
			description: "Integrated model viewer, clash detection & chat.",
			color: "bg-accent-500",
		},
		{
			icon: CreditCard,
			title: "Pay with Confidence",
			description: "Milestone escrow protects both sides.",
			color: "bg-green-500",
		},
	]),
});

type HowItWorksProps = z.input<typeof HowItWorksSchema>;

const HowItWorks: React.FC<HowItWorksProps> = (props = {}) => {
	const data = HowItWorksSchema.parse(props);

	return (
		<section className="bg-white py-20">
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				<div className="mb-16 text-center">
					<h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">{data.header}</h2>
					<p className="mx-auto max-w-3xl text-xl text-gray-600">{data.description}</p>
				</div>

				<div className="relative">
					{/* Connection Lines */}
					<div className="absolute left-0 right-0 top-1/2 hidden h-0.5 bg-gradient-to-r from-primary-500 via-secondary-500 to-accent-500 lg:block"></div>

					<div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
						{data.steps.map((step, index) => (
							<div key={index} className="group relative">
								<div className="flex flex-col items-center text-center">
									{/* Step Number */}
									<div className="relative z-10 mb-6">
										<div
											className={`h-20 w-20 ${step.color} flex items-center justify-center rounded-full shadow-lg transition-transform group-hover:scale-110`}
										>
											<step.icon className="h-10 w-10 text-white" />
										</div>
										<div className="absolute -right-2 -top-2 flex h-8 w-8 items-center justify-center rounded-full border-2 border-gray-100 bg-white shadow-md">
											<span className="text-sm font-bold text-gray-700">{index + 1}</span>
										</div>
									</div>

									<h3 className="mb-3 text-xl font-bold text-gray-900">{step.title}</h3>
									<p className="leading-relaxed text-gray-600">{step.description}</p>
								</div>
							</div>
						))}
					</div>
				</div>

				<div className="mt-12 text-center">
					<button className="rounded-lg bg-primary-500 px-8 py-4 text-lg font-semibold text-white transition-colors hover:bg-primary-600">
						{data.buttonText}
					</button>
				</div>
			</div>
		</section>
	);
};

export default HowItWorks;
